import { cx } from "../app/format";
import type { OnboardingStepId, StepWayfinderProps, WayfinderStep } from "./contracts";

const stepTone = (step: WayfinderStep, currentStep: OnboardingStepId): string => {
  const state = step.id === currentStep ? "active" : step.state;

  if (state === "done") {
    return "border-[#D7E7D1] bg-[#EAF1E6] text-[#4E7644] hover:border-[#BFD8B6]";
  }

  if (state === "active") {
    return "border-[var(--ink)] bg-[var(--ink)] text-[var(--onSolid)]";
  }

  if (state === "locked") {
    return "cursor-not-allowed border-[var(--line2)] bg-[var(--raised2)] text-[var(--ink2)]";
  }

  return "border-[var(--line3)] bg-[var(--surface)] text-[var(--ink2)] hover:border-[var(--line)] hover:bg-[var(--chip)]";
};

const stepMarkerTone = (step: WayfinderStep, currentStep: OnboardingStepId): string => {
  const state = step.id === currentStep ? "active" : step.state;

  if (state === "done") {
    return "border-[#4E7644] bg-[#4E7644] text-white";
  }

  if (state === "active") {
    return "border-[var(--onSolid)] bg-[var(--onSolid)] text-[var(--ink)]";
  }

  if (state === "locked") {
    return "border-[var(--line3)] bg-[var(--raised)] text-[var(--faint)]";
  }

  return "border-[var(--line3)] bg-[var(--surface)] text-[var(--faint)]";
};

const connectorTone = (
  previous: WayfinderStep | undefined,
  current: WayfinderStep,
  currentStep: OnboardingStepId
): string => {
  if (!previous) {
    return "";
  }

  const previousActive = previous.id === currentStep || previous.state === "done";
  const currentActive = current.id === currentStep || current.state === "done";

  return previousActive && currentActive ? "bg-[#4E7644]" : "bg-[var(--line3)]";
};

export function StepWayfinder({
  step,
  steps,
  onStepClick,
  label = "Onboarding steps",
  className
}: StepWayfinderProps) {
  return (
    <nav
      aria-label={label}
      data-showme="onboarding-steps"
      className={cx("min-w-0 max-w-full", className)}
    >
      <ol className="flex min-w-0 max-w-full flex-wrap items-stretch gap-1.5">
        {steps.map((wayfinderStep, index) => {
          const current = wayfinderStep.id === step || wayfinderStep.state === "active";
          const done = wayfinderStep.state === "done" && !current;
          const locked = wayfinderStep.state === "locked";
          const previous = steps[index - 1];

          return (
            <li
              key={wayfinderStep.id}
              className="flex min-w-[min(100%,9.5rem)] flex-[1_1_9.5rem] items-center gap-1.5 sm:min-w-[10rem] 2xl:flex-none"
            >
              {previous ? (
                <span
                  aria-hidden="true"
                  data-tcs-step-connector={wayfinderStep.id}
                  className={cx(
                    "hidden h-[1.5px] w-4 flex-none self-center rounded 2xl:block",
                    connectorTone(previous, wayfinderStep, step)
                  )}
                />
              ) : null}
              <button
                type="button"
                onClick={() => onStepClick(wayfinderStep.id)}
                disabled={locked}
                aria-current={current ? "step" : undefined}
                aria-disabled={locked ? "true" : undefined}
                className={cx(
                  "flex min-h-9 w-full min-w-0 cursor-pointer items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-[12px] font-extrabold leading-none shadow-[0_1px_2px_rgba(35,33,30,.04)] transition-colors 2xl:w-auto",
                  stepTone(wayfinderStep, step)
                )}
              >
                <span
                  data-tcs-step-badge={wayfinderStep.id}
                  className={cx(
                    "flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full border-[1.5px] text-[11px] font-extrabold",
                    stepMarkerTone(wayfinderStep, step)
                  )}
                >
                  {done ? "✓" : index + 1}
                </span>
                <span className="min-w-0 whitespace-normal leading-tight">
                  {wayfinderStep.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
